import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, TextInput, View, StyleProp, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { Toggle } from "@/components/ui/Toggle";
import { MaterialIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import { getMonthName, sameDay } from "@/lib/helpers";

// Lightweight stepper that doesn't depend on ToolTipProvider,
// safe to render inside context-menu portals.
function PickerStepper({
  onStepLeft,
  onStepRight,
  center,
  style,
}: {
  onStepLeft: () => void;
  onStepRight: () => void;
  center: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  return (
    <View style={[pickerStepperStyles.container, style]}>
      <Pressable
        onPress={onStepLeft}
        style={({ hovered }) => [
          pickerStepperStyles.button,
          { borderColor: colors.border },
          hovered && { backgroundColor: colors.border },
        ]}
      >
        <MaterialIcons name="chevron-left" size={20} color={colors.text} />
      </Pressable>
      <View style={pickerStepperStyles.content}>{center}</View>
      <Pressable
        onPress={onStepRight}
        style={({ hovered }) => [
          pickerStepperStyles.button,
          { borderColor: colors.border },
          hovered && { backgroundColor: colors.border },
        ]}
      >
        <MaterialIcons name="chevron-right" size={20} color={colors.text} />
      </Pressable>
    </View>
  );
}

const pickerStepperStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    borderRadius: 6,
    gap: 4,
    alignItems: "center",
  },
  button: {
    height: 28,
    width: 28,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 6,
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export type DateTimePickEventArgs = { date: Date };
type DateTimePickerProps = {
  date?: Date | string;
  onPick?: (e: DateTimePickEventArgs) => void;
  onCancel?: () => void;
  minuteStep?: number;
  hideTime?: boolean;
  fireOnDayPick?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
};

export function DateTimePicker({
  date,
  onPick,
  onCancel,
  hideTime = false,
  minuteStep = 5,
  fireOnDayPick = true,
  minDate,
  maxDate,
}: DateTimePickerProps) {
  const { colors } = useTheme();
  const { settings } = useDateTime();
  const now = useMemo(() => new Date(), []);

  // Parse min/max dates
  const parsedMinDate = useMemo(() => {
    if (!minDate) return null;
    const d = typeof minDate === "string" ? new Date(minDate) : minDate;
    // Normalize to start of day for date comparison
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  }, [minDate]);

  const parsedMaxDate = useMemo(() => {
    if (!maxDate) return null;
    const d = typeof maxDate === "string" ? new Date(maxDate) : maxDate;
    // Normalize to end of day for date comparison
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  }, [maxDate]);

  // Check if a date is disabled (outside min/max range)
  const isDateDisabled = (d: Date): boolean => {
    const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (parsedMinDate && dateOnly < parsedMinDate) return true;
    if (parsedMaxDate && dateOnly > parsedMaxDate) return true;
    return false;
  };

  // Handle initial date - use provided date, or current time if none provided
  const initial = useMemo(() => {
    if (date) {
      return typeof date === "string" ? new Date(date) : date;
    }
    return hideTime ? new Date() : now;
  }, [date, hideTime, now]);

  const use24h = settings.timeFormat === "24";

  // Calendar nav
  const [navYear, setNavYear] = useState(initial.getFullYear());
  const [navMonth, setNavMonth] = useState(initial.getMonth() + 1); // 1-12

  // Selection (date)
  const [selYear, setSelYear] = useState(initial.getFullYear());
  const [selMonth, setSelMonth] = useState(initial.getMonth() + 1);
  const [selDay, setSelDay] = useState(initial.getDate());

  // Selection (time)
  const initH24 = initial.getHours();
  const initM = initial.getMinutes();
  const [ampm, setAmPm] = useState(initH24 >= 12 ? "PM" : "AM");
  const [hours, setHours] = useState(use24h ? initH24 : initH24 % 12 || 12);
  const [minutes, setMinutes] = useState(Math.floor(initM / minuteStep) * minuteStep);

  // Display text state (raw user input - allows free typing)
  const initHoursDisplay = use24h ? initH24 : initH24 % 12 || 12;
  const initMinutesDisplay = Math.floor(initM / minuteStep) * minuteStep;
  const [hoursText, setHoursText] = useState(String(initHoursDisplay).padStart(2, "0"));
  const [minutesText, setMinutesText] = useState(String(initMinutesDisplay).padStart(2, "0"));

  // Compose current value
  const value = useMemo(() => {
    let h24 = hours;
    if (!use24h) {
      // Convert 12-hour format to 24-hour
      if (ampm === "AM") {
        // AM: 12 -> 0, 1-11 -> 1-11
        h24 = hours === 12 ? 0 : hours;
      } else {
        // PM: 12 -> 12, 1-11 -> 13-23
        h24 = hours === 12 ? 12 : hours + 12;
      }
    }
    return new Date(selYear, selMonth - 1, selDay, h24, minutes, 0, 0);
  }, [selYear, selMonth, selDay, hours, minutes, ampm, use24h]);

  // Validate time input for Apply button (positions.tsx pattern)
  const isTimeValid = useMemo(() => {
    // Hours validation
    if (!hoursText.trim()) return false;
    const h = parseInt(hoursText, 10);
    if (isNaN(h)) return false;
    if (use24h) {
      if (h < 0 || h > 23) return false;
    } else {
      if (h < 1 || h > 12) return false;
    }

    // Minutes validation
    if (!minutesText.trim()) return false;
    const m = parseInt(minutesText, 10);
    if (isNaN(m) || m < 0 || m > 59) return false;

    return true;
  }, [hoursText, minutesText, use24h]);

  // Helpers
  const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
  const setMonthSafe = (next: number) => {
    if (next < 1) {
      setNavMonth(12);
      setNavYear((y) => y - 1);
    } else if (next > 12) {
      setNavMonth(1);
      setNavYear((y) => y + 1);
    } else {
      setNavMonth(next);
    }
  };

  const daysInMonth = new Date(navYear, navMonth, 0).getDate();
  const firstDow = new Date(navYear, navMonth - 1, 1).getDay(); // 0-6
  const lastDow = new Date(navYear, navMonth, 0).getDay();

  const leading = Array.from({ length: firstDow }, (_, i) => {
    const d = new Date(navYear, navMonth - 1, 0);
    return { date: new Date(navYear, navMonth - 1, d.getDate() - i) };
  }).reverse();

  const current = Array.from({ length: daysInMonth }, (_, i) => {
    return { date: new Date(navYear, navMonth - 1, i + 1) };
  });

  const trailing = Array.from({ length: 6 - lastDow }, (_, i) => {
    return { date: new Date(navYear, navMonth, i + 1) };
  });

  const selectDate = (d: Date, navigateToMonth = false) => {
    setSelYear(d.getFullYear());
    setSelMonth(d.getMonth() + 1);
    setSelDay(d.getDate());
    if (navigateToMonth) {
      setNavYear(d.getFullYear());
      setNavMonth(d.getMonth() + 1);
    }

    // Fire onPick immediately if fireOnDayPick is true
    if (fireOnDayPick && !navigateToMonth) {
      let h24 = hours;
      if (!use24h) {
        // Convert 12-hour format to 24-hour
        if (ampm === "AM") {
          h24 = hours === 12 ? 0 : hours;
        } else {
          h24 = hours === 12 ? 12 : hours + 12;
        }
      }

      const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h24, minutes, 0, 0);
      onPick?.({ date: newDate });
    }
  };

  // Time input handlers - allow free typing, validation happens on Apply
  const handleHoursChange = (text: string) => {
    // Only allow digits, max 2 chars
    const cleaned = text.replace(/[^\d]/g, "").slice(0, 2);
    setHoursText(cleaned);
  };

  const handleMinutesChange = (text: string) => {
    // Only allow digits, max 2 chars
    const cleaned = text.replace(/[^\d]/g, "").slice(0, 2);
    setMinutesText(cleaned);
  };

  // Apply button handler - parse, clamp, and fire callback
  const handleApply = () => {
    if (!isTimeValid) return;

    let h = parseInt(hoursText, 10);
    let m = parseInt(minutesText, 10);

    // Apply bounds for hours
    if (use24h) {
      h = clamp(h, 0, 23);
    } else {
      if (h === 0) h = 12;
      h = clamp(h, 1, 12);
    }

    // Apply bounds and snap to step for minutes
    m = clamp(m, 0, 59);
    m = Math.round(m / minuteStep) * minuteStep;
    if (m >= 60) m = 60 - minuteStep;

    // Update actual values
    setHours(h);
    setMinutes(m);

    // Sync display text to final values
    setHoursText(String(h).padStart(2, "0"));
    setMinutesText(String(m).padStart(2, "0"));

    // Compute and fire callback
    let h24 = h;
    if (!use24h) {
      if (ampm === "AM") {
        h24 = h === 12 ? 0 : h;
      } else {
        h24 = h === 12 ? 12 : h + 12;
      }
    }
    const finalDate = new Date(selYear, selMonth - 1, selDay, h24, m, 0, 0);
    onPick?.({ date: finalDate });
  };

  return (
    <View style={[styles.root]}>
      {/* Header */}
      <View style={styles.headerRow}>
        <PickerStepper
          style={{ flexGrow: 1, flex: 1 }}
          onStepLeft={() => setMonthSafe(navMonth - 1)}
          onStepRight={() => setMonthSafe(navMonth + 1)}
          center={<ThemedText>{getMonthName(navMonth, false)}</ThemedText>}
        />
        <PickerStepper
          style={{ flexGrow: 1, flex: 1 }}
          onStepLeft={() => setNavYear((y) => y - 1)}
          onStepRight={() => setNavYear((y) => y + 1)}
          center={<ThemedText>{navYear}</ThemedText>}
        />
      </View>

      {/* Days of week */}
      <View style={styles.dowRow}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, index) => (
          <ThemedText key={`dow_${index}`} style={[styles.dow, { color: colors.placeholder }]}>
            {d}
          </ThemedText>
        ))}
      </View>

      {/* Grid */}
      <View style={[styles.grid]}>
        {leading.map(({ date: d }, i) => {
          const isDisabled = isDateDisabled(d);
          return (
            <Button
              key={`l_${i}`}
              text={`${d.getDate()}`}
              onPress={() => !isDisabled && selectDate(d, true)}
              enabled={!isDisabled}
              textStyle={[{ color: colors.placeholder }, isDisabled && { textDecorationLine: "line-through" }]}
              style={[styles.cell, styles.mutedCell, isDisabled && styles.disabledCell]}
            />
          );
        })}

        {current.map(({ date: d }, i) => {
          const isToday = sameDay(d, now);
          const isSelected = d.getFullYear() === selYear && d.getMonth() + 1 === selMonth && d.getDate() === selDay;
          const isDisabled = isDateDisabled(d);

          return (
            <Button
              key={`c_${i}`}
              text={`${d.getDate()}`}
              onPress={() => !isDisabled && selectDate(d)}
              enabled={!isDisabled}
              style={[
                styles.cell,
                isSelected && !isDisabled && { backgroundColor: colors.primary },
                isToday && !isSelected && !isDisabled && { backgroundColor: colors.secondary },
                isDisabled && styles.disabledCell,
              ]}
              textStyle={[
                styles.cellText,
                isSelected && !isDisabled && { color: "white", fontWeight: "600" },
                isToday && !isSelected && !isDisabled && { color: "white" },
                isDisabled && { color: colors.placeholder, textDecorationLine: "line-through" },
              ]}
            />
          );
        })}

        {trailing.map(({ date: d }, i) => {
          const isDisabled = isDateDisabled(d);
          return (
            <Button
              key={`t_${i}`}
              text={`${d.getDate()}`}
              onPress={() => !isDisabled && selectDate(d, true)}
              enabled={!isDisabled}
              textStyle={[{ color: colors.placeholder }, isDisabled && { textDecorationLine: "line-through" }]}
              style={[styles.cell, styles.mutedCell, isDisabled && styles.disabledCell]}
            />
          );
        })}
      </View>

      {/* Time + Actions */}
      <View style={styles.footerRow}>
        {!hideTime && (
          <View style={[styles.timeWrap, { borderColor: colors.border, backgroundColor: colors.card }]}>
            <TextInput
              style={[styles.timeField, { color: colors.text }]}
              value={hoursText}
              onChangeText={handleHoursChange}
              keyboardType="numeric"
              maxLength={2}
              placeholder={use24h ? "00" : "12"}
              textAlign="center"
            />
            <ThemedText style={styles.colon}>:</ThemedText>
            <TextInput
              style={[styles.timeField, { color: colors.text }]}
              value={minutesText}
              onChangeText={handleMinutesChange}
              keyboardType="numeric"
              maxLength={2}
              placeholder="00"
              textAlign="center"
            />

            {!use24h && (
              <Toggle
                style={styles.toggle}
                choices={["AM", "PM"]}
                value={ampm}
                onPick={(item) => setAmPm(item as "AM" | "PM")}
              />
            )}

            <Button
              text="Now"
              icon="clock-outline"
              style={[styles.nowBtn, { backgroundColor: colors.backgroundSecondary }]}
              onPress={() => {
                const n = new Date();
                setNavYear(n.getFullYear());
                setNavMonth(n.getMonth() + 1);
                setSelYear(n.getFullYear());
                setSelMonth(n.getMonth() + 1);
                setSelDay(n.getDate());

                const newMinutes = Math.floor(n.getMinutes() / minuteStep) * minuteStep;
                setMinutes(newMinutes);
                setMinutesText(String(newMinutes).padStart(2, "0"));

                if (use24h) {
                  setHours(n.getHours());
                  setHoursText(String(n.getHours()).padStart(2, "0"));
                } else {
                  setAmPm(n.getHours() >= 12 ? "PM" : "AM");
                  const h12 = n.getHours() % 12 || 12;
                  setHours(h12);
                  setHoursText(String(h12).padStart(2, "0"));
                }
              }}
            />
          </View>
        )}

        <View style={styles.actions}>
          {onCancel && (
            <Button text="Cancel" style={[styles.actionBtn, { backgroundColor: colors.card }]} onPress={onCancel} />
          )}
          {!hideTime && (
            <Button
              text="Apply"
              icon="check"
              enabled={isTimeValid}
              style={[styles.actionBtn, { backgroundColor: colors.primary }]}
              textStyle={{ color: "white" }}
              onPress={handleApply}
            />
          )}
        </View>
      </View>
    </View>
  );
}

/* ---------------- styles ---------------- */
const CELL = 36;

const styles = StyleSheet.create({
  root: { width: 300, gap: 10 },
  headerRow: { flexDirection: "row", gap: 8 },
  pill: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 6,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  headerText: { fontSize: 16, fontWeight: "600" },
  navBtn: { backgroundColor: "transparent", minWidth: 30, minHeight: 30 },
  dowRow: {
    flexDirection: "row",
    gap: 4,
    paddingHorizontal: 2,
  },
  dow: {
    flex: 1,
    width: "14%",
    textAlign: "center",
    fontSize: 12,
    fontWeight: "600",
    opacity: 0.7,
    alignItems: "center",
    justifyContent: "center",
    alignContent: "center",
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  cell: {
    width: `${(100 - 6 * 4) / 7}%`,
    minWidth: CELL,
    height: CELL,
    borderWidth: 0,
    borderRadius: 8,
    justifyContent: "center",
    paddingLeft: 8,
  },
  cellText: { textAlign: "left" },
  mutedCell: { opacity: 0.5 },
  disabledCell: { opacity: 0.3 },
  footerRow: { gap: 10 },
  timeWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    padding: 8,
  },
  timeField: {
    width: 50,
    textAlign: "center",
    fontSize: 16,
    fontWeight: "600",
  },
  timeInput: { height: 28, fontSize: 14, borderWidth: StyleSheet.hairlineWidth },
  colon: { fontSize: 18, fontWeight: "600", opacity: 0.8 },
  toggle: {},
  nowBtn: { marginLeft: "auto", paddingHorizontal: 12, minHeight: 32, borderRadius: 8 },
  actions: { flexDirection: "row", gap: 8 },
  actionBtn: { flex: 1, justifyContent: "space-evenly", paddingVertical: 2 },
});
